import { LitElement, html } from 'lit';
import styles from './studio-button-group.css.mjs';

export class StudioButtonGroup extends LitElement {
  static styles = styles;

  static properties = {
    size: { type: String, reflect: true },
    styleType: { type: String, reflect: true, attribute: 'style-type' },
    max: { type: Number, reflect: true },
  };

  static validSizes = ['giant', 'large', 'medium', 'small'];
  static validStyleTypes = ['filled', 'outline', 'clear'];

  constructor() {
    super();
    this.size = 'medium';
    this.styleType = 'filled';
    this.max = 10; // No default limit
  }

  set size(value) {
    this._size = StudioButtonGroup.validSizes.includes(value) ? value : 'medium';
  }

  get size() {
    return this._size;
  }

  set styleType(value) {
    this._styleType = StudioButtonGroup.validStyleTypes.includes(value) ? value : 'filled';
  }

  get styleType() {
    return this._styleType;
  }

  set max(value) {
    this._max = value > 1 && value < 11 ? value : 10;
  }

  get max() {
    return this._max;
  }

  render() {
    return html`
      <div class="button-group" part="button-group" role="group">
        <slot @slotchange=${this._onSlotChange}></slot>
        <slot></slot>
      </div>
    `;
  }

  _onSlotChange() {
    const buttons = this._getSlottedButtons();
    buttons.forEach((button, index, arr) => {
      const effectiveMax = Math.min(arr.length, this.max);
      if (index >= effectiveMax) {
        button.style.display = 'none';
        return;
      }
      button.style.display = '';

      // Update position only if changed
      const newPosition = index === 0 ? 'left' : index === effectiveMax - 1 ? 'right' : 'center';
      if (button.position !== newPosition) {
        button.position = newPosition;
      }

      // Forward group props if not explicitly set
      if (!button.hasAttribute('size') && button.size !== this.size) {
        button.setAttribute('size', this.size);
      }
      if (!button.hasAttribute('style-type') && button.styleType !== this.styleType) {
        button.setAttribute('style-type', this.styleType);
      }

      // Ensure part export
      if (!button.hasAttribute('exportparts')) {
        button.setAttribute('exportparts', 'button');
      }
    });
  }

  _getSlottedButtons() {
    const slot = this.shadowRoot.querySelector('slot');
    return slot
      .assignedElements({ flatten: true })
      .filter(el => el.tagName.toLowerCase() === 'studio-button');
  }
}

customElements.define('studio-button-group', StudioButtonGroup);